<template>
  <div class="content">
    <h1 class="has-text-centered">Netzwerk Zugriffsverfahren</h1>
    <div class="box">
      <p>Eine kompakte Zusammenfassung über CSMA/CD und Token Passing.</p>
      <FAQsTableOfContents :faqs="faqs" />
    </div>

    <div class="box">
      <FAQs :faqs="faqs" :h_level="2" />
    </div>

    <FeedbackBox />
  </div>
</template>

<script>
import FeedbackBox from '@/components/FeedbackBox.vue'
import FAQs from '@/components/FAQs.vue'
import FAQsTableOfContents from '@/components/FAQsTableOfContents.vue'

export default {
  name: 'theory-network-access-methods',
  components: {
    FeedbackBox,
    FAQs,
    FAQsTableOfContents
  },
  metaInfo: {
    title: 'Netzwerk Zugriffsverfahren | Elektrotechnik-Rechner.at',
    meta: [
      {
        name: 'description',
        content: 'Eine kompakte Zusammenfassung über CSMA/CD und Token Passing.'
      }
    ]
  },
  created () {
    this.$store.commit('updateNavTitle', { title: '<span class="fas fa-angle-left"></span> Zurück', route: 'network-engineering' })
  },
  data () {
    return {
      faqs: [
        {
          question: 'Was sind Zugriffsverfahren in einem Netzwerk?',
          answer:
            '<ul>' +
            '  <li>Durch Hardware und Adressierung ist es möglich in einem Netzwerk das Zielsystem zu erreichen</li>' +
            '  <li>Um Probleme und Datenverlust beim gleichzeitigen Senden von mehreren Stationen zu verhindern wurden Zugriffsverfahren entwickelt</li>' +
            '  <li><strong>Nichtdeterministische oder stochastisches Verfahren</strong></li>' +
            '  <ul>' +
            '    <li>Alle Stationen sind gleichberechtigt und können zu jeder Zeit Daten in das Netz senden</li>' +
            '    <li>Wenn Störungen auftreten wird der Sendeversuch nach einer bestimmten Wartezeit wiederholt</li>' +
            '    <li>Wiederholungen und Wartezeiten verschlechtern Übertragungsrate und damit die Netzperformance</li>' +
            '  </ul>' +
            '  <li><strong>Deterministisches Verfahren</strong></li>' +
            '  <ul>' +
            '    <li>Nur eine Station welche das Senderecht erhalten hat darf senden</li>' +
            '    <li>Nach Datenempfang wird das Senderecht weitergegeben</li>' +
            '  </ul>' +
            '</ul>'
        },
        {
          question: 'Was ist CSMA/CD?',
          answer:
            '<ul>' +
            '  <li>CSMA/CD = <strong>C</strong>arrier <strong>S</strong>ense <strong>M</strong>ultiple <strong>A</strong>ccess/<strong>C</strong>ollision <strong>D</strong>etection</li>' +
            '  <li>Eine Erweiterung von CSMA</li>' +
            '  <li>Asynchrones Medienzugriffsverfahren</li>' +
            '  <li>Nichtdeterministisches Verfahren</li>' +
            '  <li>Regelt den Zugriff verschiedener Stationen (beispielsweise Computer samt ihrer Netzwerkanschlüsse) auf ein gemeinsames Übertragungsmedium</li>' +
            '  <li>Funktioniert mit jeder Netztopologie</li>' +
            '</ul>'
        },
        {
          question: 'Wie funktioniert CSMA/CD?</h2>',
          answer:
            '<ul>' +
            '  <li>CS = <strong>C</strong>arrier <strong>S</strong>ense</li>' +
            '  <ul>' +
            '    <li>Jede Station überprüft anhand von Spannungswerten, ob das Übertragungsmedium frei ist</li>' +
            '    <li>Bei feststellen eines Datenverkehrs wird auf Übertragungsende und weitere 9,6 µs ("Inter Frame Gap") gewartet</li>' +
            '  </ul>' +
            '  <li>MA = <strong>M</strong>ultiple <strong>A</strong>ccess</li>' +
            '  <ul>' +
            '    <li>Wenn das Übertragungsmedium spannungsfrei ist, wird ein adressiertes Datenpaket verschickt</li>' +
            '    <li>Wenn eine zweite Station ebenfalls zum gleichen Zeitpunkt eine Übertragung durchführen will, kommt es zu Datenüberlagerung</li>' +
            '  </ul>' +
            '  <li>CD = <strong>C</strong>ollision <strong>D</strong>etect</li>' +
            '  <ul>' +
            '    <li>Erkennt Kollisionen (Datenüberlagerungen)</li>' +
            '  </ul>' +
            '</ul>' +
            '<h3>Ablauf einer Übertragung</h3>' +
            '<p>Wenn ein Gerät Daten senden möchte, hält es sich an folgenden Ablauf:</p>' +
            '<ul>' +
            '  <li><strong>Schritt 1: Horchen</strong></li>' +
            '  <ul>' +
            '    <li>Übertragungsmedium wird überwacht:</li>' +
            '    <ul>' +
            '      <li><strong>Frei:</strong> Wenn es eine bestimmte Zeit lang ("Interframe Spacing") frei ist, weiter mit Schritt 2</li>' +
            '      <li><strong>Belegt:</strong> Bleibt bei Schritt 1</li>' +
            '    </ul>' +
            '  </ul>' +
            '  <li><strong>Schritt 2: Senden</strong></li>' +
            '  <ul>' +
            '    <li>Übertragung startet</li>' +
            '    <li>Übertragungsmedium wird währenddessen weiter überwacht</li>' +
            '    <li>Zwei Fälle können auftreten</li>' +
            '    <ul>' +
            '      <li>' +
            '        <strong>Erfolg:</strong>' +
            '        Übertragung ist erfolgreich und kollisionslos abgeschlossen, weiter mit Schritt 5' +
            '      </li>' +
            '      <li>' +
            '        <strong>Kollision:</strong> Datenübertragung wird beendet und mit einem kurzen, definierten Störsignal ("jam") den anderen Stationen Bescheid gegeben, weiter mit Schritt 3</li>' +
            '    </ul>' +
            '  </ul>' +
            '  <li><strong>Schritt 3: Leitung ist belegt</strong></li>' +
            '  <ul>' +
            '    <li>Anzahl der Übertragungsversuche werden überprüft</li>' +
            '    <ul>' +
            '      <li><strong>Maximum nicht erreicht:</strong> Zufällige Zeit abwarten und wieder mit Schritt 1 beginnen</li>' +
            '      <li><strong>Maximum erreicht:</strong> Weiter mit Schritt 4</li>' +
            '    </ul>' +
            '  </ul>' +
            '  <li><strong>Schritt 4: Fehler</strong></li>' +
            '  <ul>' +
            '    <li>Maximale Anzahl an Übertragungsversuchen wurde erreicht</li>' +
            '    <li>Fehler wird an höhere Netzwerkschichten gemeldet, weiter mit Schritt 5</li>' +
            '  </ul>' +
            '  <li><strong>Schritt 5: Ende</strong></li>' +
            '  <ul>' +
            '    <li>Übertragungsmodus wird verlassen</li>' +
            '  </ul>' +
            '</ul>'
        },
        {
          question: 'Was ist Token Passing?',
          answer:
            '<ul>' +
            '  <li>Medienzugriffsverfahren in Rechnernetzen</li>' +
            '  <li>Grundlage von Token Passing ist das "Token" - es wird in einem Rechnernetz von einer Station zur benachbarten Station in einer Ringtopologie weitergeleitet</li>' +
            '  <li>Zwei Realisierungsformen</li>' +
            '  <ul>' +
            '    <li><strong>Token Ring:</strong> Der Nachbar ist die physisch nächste erreichbare Station</li>' +
            '    <li><strong>Token Bus:</strong> Der Nachbar ist die logisch nächste erreichbare Station (realisiert durch die Adressen der Netzwerkkarte)</li>' +
            '  </ul>' +
            '</ul>' +
            '<h3>Ablauf</h3>' +
            '<img src="/img/theory/Token-Passing.png">' +
            '<ol>' +
            '  <li>Ein Token wird ständig im Kreis, von Nachbar zu Nachbar, geschickt&nbsp;</li>' +
            '  <li>Möchte Computer A Daten an Computer C übermitteln, wartet er das freie Token ab, hängt das Datenpaket an und adressiert es an Computer C - der Token ist nun besetzt</li>' +
            '  <li>Computer A gibt das gesamte Paket an Computer B weiter</li>' +
            '  <li>Computer B erkennt, dass das Paket nicht für ihn bestimmt ist und gibt es an Computer C weiter</li>' +
            '  <li>Computer erkennt dass das Paket für ihn bestimmt ist, kopiert die Daten, ändert das Token auf empfangen und schickt es wieder auf den Ring</li>' +
            '  <li>Computer kontrolliert den "Empfangen"-Status des Tokens und gibt ihn wieder frei</li>' +
            '</ol>'
        }
      ]
    }
  }
}
</script>
